import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getQueryFn } from '../lib/queryClient';
import { useLocation } from 'wouter';

interface UserCompany {
  id: number;
  name: string;
  industry_name: string | null;
  role: string;
  system_role: string | null;
}

interface CompanySelectorProps {
  currentCompanyId?: number;
  onCompanyChange?: (companyId: number) => void;
}

export function CompanySelector({ currentCompanyId, onCompanyChange }: CompanySelectorProps) {
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const { data: userCompanies, isLoading } = useQuery<UserCompany[]>({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
  });

  // Don't show selector if user has only one company or is system user
  const shouldShowSelector = userCompanies && userCompanies.length > 1 && 
    userCompanies.some(company => !company.system_role);

  const currentCompany = userCompanies?.find(company => company.id === currentCompanyId);

  const handleCompanySelect = (companyId: number) => {
    setIsOpen(false);
    if (onCompanyChange) {
      onCompanyChange(companyId);
    } else {
      // Default behavior: navigate to company profile
      setLocation(`/company-profile/${companyId}`);
    }
  };

  if (!shouldShowSelector || isLoading) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <i data-feather="building" className="w-3 h-3"></i>
        <span className="max-w-24 truncate">
          {currentCompany?.name || 'Select Company'}
        </span>
        <i data-feather="chevron-down" className="w-3 h-3"></i>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
            <div className="py-1">
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                Your Companies
              </div>
              
              {userCompanies?.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleCompanySelect(company.id)}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                    company.id === currentCompanyId ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {company.name}
                      </div>
                      {company.industry_name && (
                        <div className="text-xs text-gray-500 truncate">
                          {company.industry_name}
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        company.system_role
                          ? 'bg-purple-100 text-purple-800'
                          : company.role === 'COMPANY_ADMIN'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {company.system_role || company.role}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}



